import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  RecaptchaVerifier,
  linkWithPhoneNumber,
  signInWithPhoneNumber,
  type ConfirmationResult,
} from 'firebase/auth';
import { Loader2, Phone, Shield, CheckCircle2 } from 'lucide-react';
import { auth } from '../services/firebase';
import { redeemReferral } from '../services/referralsService';
import { useAuth } from '../contexts/AuthContext';

/**
 * Phone verification gate.
 *
 * Two call sites, both on the /referrals page:
 *
 *   - `referred` = true   → the user arrived via someone's code. On success
 *                           they get 5 credits, the referrer gets 5 credits,
 *                           and their own shareable code is activated.
 *   - `referred` = false  → the user signed up organically. Verification
 *                           activates their shareable code only (no bonus);
 *                           paying for phone verify would rebuild the abuse
 *                           incentive we're gating against.
 *
 * Firebase Phone Auth requires an invisible reCAPTCHA — we mount it to a
 * hidden div, kick off SMS via `linkWithPhoneNumber` (so the phone credential
 * is attached to the existing Google account, not a separate one), and on
 * success call /referrals/redeem, which the backend routes to whichever path
 * applies.
 *
 * We prefer `linkWithPhoneNumber` over `signInWithPhoneNumber` because the
 * user is already authenticated via Google — linking keeps it one account.
 * If linking fails because the phone is already on another account, we
 * surface a clear error.
 */
export default function PhoneVerify({ onDone, referred = false }: { onDone?: () => void; referred?: boolean }) {
  const { refresh } = useAuth();
  const recaptchaRef = useRef<HTMLDivElement | null>(null);
  const verifierRef = useRef<RecaptchaVerifier | null>(null);
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [confirmation, setConfirmation] = useState<ConfirmationResult | null>(null);
  const [stage, setStage] = useState<'enter' | 'code' | 'done'>('enter');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Build the invisible reCAPTCHA once on mount. Firebase allows only one
  // verifier per container, so we stash it in a ref and clear on unmount.
  useEffect(() => {
    if (!recaptchaRef.current || verifierRef.current) return;
    try {
      verifierRef.current = new RecaptchaVerifier(auth, recaptchaRef.current, {
        size: 'invisible',
      });
    } catch (err) {
      console.warn('[phoneVerify] recaptcha init failed', err);
    }
    return () => {
      verifierRef.current?.clear();
      verifierRef.current = null;
    };
  }, []);

  const sendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = phone.trim();
    if (!/^\+[1-9]\d{6,14}$/.test(trimmed)) {
      setError('Please enter a full international number, e.g. +919876543210');
      return;
    }
    if (!verifierRef.current) {
      setError('reCAPTCHA is not ready yet — please wait a moment and try again.');
      return;
    }
    setBusy(true); setError(null);
    try {
      const fbUser = auth.currentUser;
      // Link onto the signed-in account so the user keeps their Google
      // identity. If they're somehow not signed in (e.g. token expired mid-
      // flow), fall back to sign-in-with-phone so they at least get verified.
      const conf = fbUser
        ? await linkWithPhoneNumber(fbUser, trimmed, verifierRef.current)
        : await signInWithPhoneNumber(auth, trimmed, verifierRef.current);
      setConfirmation(conf);
      setStage('code');
    } catch (err: any) {
      setError(friendlyPhoneError(err));
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!confirmation) return;
    if (!/^\d{6}$/.test(otp.trim())) {
      setError('Enter the 6-digit code from your SMS.');
      return;
    }
    setBusy(true); setError(null);
    try {
      await confirmation.confirm(otp.trim());
      // Force a token refresh so the Firebase ID token picks up the new
      // phone credential before we call /referrals/redeem.
      await auth.currentUser?.getIdToken(true);
      const result = await redeemReferral();
      // Toast message branches on whether credits actually moved. Organic
      // verify (no referrer, or referrer cap already hit) lands with
      // credited === 0 and we want a clear non-misleading message.
      if (result.credited > 0) {
        toast.success(`Verified — ${result.credited} credits added to your account, ${result.referrerCredited} credits to your friend.`);
      } else {
        toast.success('Phone verified — your referral link is ready to share.');
      }
      await refresh();
      setStage('done');
      onDone?.();
    } catch (err: any) {
      setError(friendlyPhoneError(err));
    } finally {
      setBusy(false);
    }
  };

  if (stage === 'done') {
    return (
      <div className="card p-6 flex items-center gap-3 bg-emerald-50 border-emerald-100">
        <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0" />
        <div>
          <div className="font-semibold text-emerald-800">Phone verified</div>
          <div className="text-sm text-emerald-700">
            {referred
              ? 'Your 5 bonus credits have landed, your friend received 5 too, and your own referral link is now active.'
              : 'Your referral link is now active — you can share it to earn credits when friends sign up.'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card p-6">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-brand-50 text-brand-700 grid place-items-center flex-shrink-0">
          <Shield className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold">
            {referred ? 'Verify your phone to unlock your bonus + referral link' : 'Verify your phone to activate your referral link'}
          </h3>
          <p className="text-sm text-ink-600 mt-1">
            {referred
              ? 'One quick SMS — it unlocks 5 credits for you, 5 for the friend who invited you, and activates your own shareable link so you can refer others.'
              : 'One quick SMS — it activates your shareable referral link so you can invite friends and earn credits.'}
            {' '}Each phone number can only activate the referral system on one GradeLab account, ever.
          </p>

          {stage === 'enter' && (
            <form onSubmit={sendOtp} className="mt-4 space-y-3">
              <div>
                <label className="label">Phone number</label>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-ink-400" />
                  <input
                    className="input flex-1"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+91 98765 43210"
                    inputMode="tel"
                    autoComplete="tel"
                  />
                </div>
                <p className="text-xs text-ink-500 mt-1">
                  Include your country code, e.g. <code>+91</code> for India, <code>+65</code> for Singapore, <code>+1</code> for the US.
                </p>
              </div>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <button type="submit" disabled={busy} className="btn-primary">
                {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending code…</> : 'Send verification code'}
              </button>
            </form>
          )}

          {stage === 'code' && (
            <form onSubmit={verifyOtp} className="mt-4 space-y-3">
              <div>
                <label className="label">Enter the 6-digit code</label>
                <input
                  className="input"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                />
                <p className="text-xs text-ink-500 mt-1">
                  Sent to {phone}. <button type="button" className="underline" onClick={() => { setStage('enter'); setConfirmation(null); setOtp(''); }}>Change number</button>
                </p>
              </div>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <button type="submit" disabled={busy} className="btn-primary">
                {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Verifying…</> : 'Verify and claim credits'}
              </button>
            </form>
          )}
        </div>
      </div>
      {/* Invisible reCAPTCHA anchor — Firebase mounts into this div. */}
      <div ref={recaptchaRef} />
    </div>
  );
}

function friendlyPhoneError(err: any): string {
  // Firebase error codes the user is most likely to hit.
  const code = err?.code ?? '';
  if (code === 'auth/invalid-phone-number')   return 'That phone number looks invalid. Include the country code, e.g. +919876543210.';
  if (code === 'auth/missing-phone-number')   return 'Please enter a phone number.';
  if (code === 'auth/quota-exceeded')         return 'Too many SMS sent from this device. Try again in a few minutes.';
  if (code === 'auth/too-many-requests')      return 'Too many attempts. Please wait a minute and try again.';
  if (code === 'auth/invalid-verification-code') return 'That code is incorrect. Double-check the SMS.';
  if (code === 'auth/code-expired')           return 'Your code expired. Request a new one.';
  if (code === 'auth/credential-already-in-use' || code === 'auth/account-exists-with-different-credential') {
    return 'This phone number is already linked to another GradeLab account. Sign in with that account, or use a different number.';
  }
  if (code === 'auth/provider-already-linked') return 'A phone number is already linked to this account.';
  // Server-side 409 (phone already used for a referral)
  const apiMsg = err?.response?.data?.error;
  if (apiMsg) return apiMsg;
  return err?.message ?? 'Something went wrong. Please try again.';
}
